import React from 'react';
import { ContentSection } from '@thd-olt-component-react/content-section';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { CapabilityCardSection } from '../components/CapCardSection/CapabilityCardSection';
import { ContentAccordionSection } from '../components/ContentAccordionSection/ContentAccordionSection';

// Function to create an ImpressionProvider component if the component type is impressable
const createImpressionProvider = (component, position, newDynamicComponent) => {
  const impressableComponents = ['HeroCarousel', 'Section', 'ProductShelf'];
  const typename = component.__typename;

  if (impressableComponents.includes(typename)) {
    let componentName = typename === 'ProductShelf' ? typename : 'Section';
    const impressionData = {
      id: component.id,
      component: componentName,
      name: componentName,
      type: 'content'
    };

    return (
      <ImpressionProvider key={position || 1} data={impressionData}>
        {newDynamicComponent}
      </ImpressionProvider>
    );
  }

  return newDynamicComponent;
};

// Helper function to get component props
const getComponentProps = (typename, component, componentProps, customProps, promoVisNavTitles) => {
  const hasSideNav = component.numberOfCardsAcross !== '6';
  return {
    ...componentProps,
    ...(typename.toString().toLocaleLowerCase() === 'herocarousel' && { title: null }),
    ...(typename === 'Hero' && { heightClass: 'hero__shorter-no-sidenav' }),
    ...(typename === 'CapabilityCard' && componentProps?.capCardsHorizontal && { isHorizontal: true }),
    ...(typename === 'VisualNavigation' && { isInSection: true }),
    ...(typename === 'EndCap' && { entryId: component.id }),
    ...(typename === 'Description_New' && {
      title: component.title,
      description: component.descriptionText,
      descriptionRichText: component.description,
      useDefaultFontStylesForHeadings: true
    }),
    ...(typename === 'PromoVisualNavigation' && {
      title: promoVisNavTitles[component.id] || componentProps.dynamicPromoVisNavTitle,
      experienceHasSideNav: hasSideNav,
    }),
    ...customProps[typename] && customProps[typename](component)
  };
};

// Function to render a component with its props and children
const renderComponent = (component, position, componentMapper, customProps, promoVisNavTitles, componentProps = {}) => {
  const typename = component.__typename;

  if (!componentMapper.getComponent(typename)) {
    return null;
  }

  const allComponentProps = getComponentProps(typename, component, componentProps, customProps, promoVisNavTitles);

  const newDynamicComponent = React.createElement(
    componentMapper.getComponent(typename),
    {
      componentId: component.id,
      componentClass: typename,
      componentPosition: position,
      key: position,
      ...allComponentProps
    },
    component.components?.map((comp, index) => renderComponent(comp, index + 1,
      componentMapper, customProps, promoVisNavTitles))
  );

  return createImpressionProvider(component, position, newDynamicComponent);
};

// Function to add a dynamic section to the Content object
const addDynamicSection = (dynamicSection, keyName, contentObj, componentMapper, customProps, promoVisNavTitles) => {
  const newContent = { ...contentObj };
  newContent[keyName] = [];

  dynamicSection.forEach((subSection) => {
    let { isCarousel, title, id } = subSection;

    let subSectionProps = { ...subSection };

    if (isCarousel) {
      subSectionProps = {
        ...subSectionProps,
        spaceBetweenXs: 16,
        spaceBetweenSm: 24,
        spaceBetweenMd: 24,
        spaceBetweenLg: 32,
        spaceBetweenXl: 32,
        slidesLg: 3,
        slidesXl: 3,
        slidesMd: 3,
        slidesSm: 2,
        slidesXs: 2
      };
    }

    let capCardsHorizontal;
    const firstComponent = subSection.components !== null ? subSection.components[0] : [];
    let componentName = firstComponent?.__typename;
    const dynamicPromoVisNavTitle = componentName === 'PromoVisualNavigation' ? title : '';
    let SectionType;

    switch (componentName) {
    case 'ContentAccordion':
      SectionType = ContentAccordionSection;
      break;
    case 'CapabilityCard':
      SectionType = CapabilityCardSection;
      capCardsHorizontal = subSection.components?.length === 1;
      break;
    default:
      SectionType = ContentSection;
      break;
    }

    const childSection = React.createElement(
      SectionType,
      { isCarousel, title, ...subSectionProps },
      subSection.components?.map((comp, index) => renderComponent(comp, index + 1,
        componentMapper, customProps, promoVisNavTitles, {
          capCardsHorizontal,
          dynamicPromoVisNavTitle
        }))
    );
    newContent[keyName].push(() => React.createElement(
      ImpressionProvider,
      {
        data: {
          id,
          component: 'Section',
          type: 'content',
          name: 'Section'
        }
      },
      childSection
    ));
  });

  return newContent;
};

// Function to add a component to the Content object
const addComponent = (componentName, component, contentObj, componentMapper, customProps, promoVisNavTitles) => {
  let { isCarousel, title, ...otherProps } = component;
  let isSection = component.__typename === 'Section' || component.__typename === 'HeroCarousel';

  const newContent = { ...contentObj };
  newContent[componentName] = (props) => renderComponent(component, 1,
    componentMapper, customProps, promoVisNavTitles, {
      ...isSection && { isCarousel, title, ...otherProps },
      ...props
    });

  return newContent;
};

// Helper function to handle content section
const handleContentSection = (contentSection, key, contentObj, componentMapper, customProps, promoVisNavTitles) => {
  const typename = contentSection?.__typename;
  let newContent = { ...contentObj };

  if (Array.isArray(contentSection)) {
    newContent = addDynamicSection(contentSection, key, newContent, componentMapper, customProps, promoVisNavTitles);
  } else if (componentMapper.getComponent(typename)) {
    const componentName = typename === 'Section' ? key : typename;
    newContent = addComponent(componentName, contentSection,
      newContent, componentMapper, customProps, promoVisNavTitles);
  } else if (!contentSection) {
    if (componentMapper.isValidKey(key)) {
      const isDynamicSection = key.includes('dynamicSections');
      const contentKey = componentMapper.getContentKey(key);
      newContent[contentKey] = isDynamicSection ? [] : () => React.Fragment;
    } else {
      newContent[key] = () => React.Fragment;
    }
  }
  return newContent;
};

// Main function to parse content and build the Content object
export const parseContent = ({ data, componentMapper, customProps = {} }) => {
  if (!data?.layouts?.content) {
    return {};
  }

  const { content } = data.layouts;

  let Content = {};
  const promoVisNavTitles = {};

  Object.keys(content).forEach((key) => {
    const contentSection = content[key];
    Content = handleContentSection(contentSection, key, Content, componentMapper, customProps, promoVisNavTitles);
  });

  return {
    Content: {
      ...Content,
      heroCarousel: Content.heroCarousel || (() => React.Fragment),
      HeroCarousel: Content.HeroCarousel || (() => React.Fragment)
    }
  };
};

// Function to validate if a slug contains 'N-'
export const validateNValue = (slug) => {
  return slug ? slug.includes('N-') : false;
};
